import { ulid } from 'ulid'
import { useCallback, useReducer, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Card from '@/components/common/Card'
import DropZone from '@/components/mediaBuying/adCreatives/screens/uploadAssets/upload/DropZone'
import FilesList from '@/components/mediaBuying/adCreatives/screens/uploadAssets/upload/FilesList'
import { useSubscription } from '@apollo/client'
import { showNotification } from '@/utils/notifications'
import { adAssetUploadReducer, initialState, ACTIONS, FILE_STATES } from '@/reducers/adAssetUploadReducer'
import {
    UPLOAD_FILES,
    DELETE_AD_ASSET,
    AD_ASSET_UPLOAD_SUBSCRIPTION,
    CANCEL_UPLOAD,
    GET_AD_ASSETS,
} from '@/graphql/ads/ad-assets-queries'
import { CubeLoader } from '@/components/ui/cube-loader'
import { Button } from '@/components/ui'

const Upload = ({ campaignId, adId, campaignFormat, onUploadComplete, onCancel }) => {
    const [state, dispatch] = useReducer(adAssetUploadReducer, initialState)
    const { uploadId, files } = state
    const [uploadFiles] = useMutation(UPLOAD_FILES)
    const [deleteAdAsset] = useMutation(DELETE_AD_ASSET)
    const [cancelUpload] = useMutation(CANCEL_UPLOAD)
    const [formState, setFormState] = useState('initial')
    const [adAssets, setAdAssets] = useState([])
    const [adAssetsLoading, setAdAssetsLoading] = useState(true)
    const [uploadStarted, setUploadStarted] = useState(false)

    useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId, hasMetaId: false, source: 'uploadForAi' },
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setAdAssets(data.AdAsset?.adAssets || [])
            setAdAssetsLoading(false)
        },
    })

    useEffect(() => {
        const isUploading = files.some((file) => file.state === FILE_STATES.UPLOADING)
        const hasFilesToUpload = files.some((file) => file.isNew && file.state === FILE_STATES.INITIAL)
        const allFilesUploaded = files.every((file) => file.state === FILE_STATES.UPLOADED)

        if (isUploading) setFormState('uploading')
        else if (hasFilesToUpload) setFormState('dirty')
        else if (allFilesUploaded && files.length > 0 && uploadStarted) {
            setFormState('clean')
            // Add a small delay to ensure UI state synchronization before notifying parent
            setTimeout(() => {
                if (onUploadComplete) onUploadComplete()
                showNotification('Image(s) uploaded successfully', 'success', 3000, null, null, false, true)
            }, 100)
        } else setFormState('initial')
    }, [files, onUploadComplete])

    useEffect(() => {
        adAssets.forEach((asset) => {
            const payload = {
                id: asset.id,
                isNew: false,
                name: asset.name,
                uploadedKB: asset.size / 1024,
                totalKB: asset.size / 1024,
                width: asset.width,
                height: asset.height,
                thumbnails: asset.thumbnails,
                url: asset.url,
                state: FILE_STATES.UPLOADED,
                type: asset.type,
            }

            dispatch({ type: ACTIONS.ADD_FILE, payload })
        })
    }, [adAssets])

    useSubscription(AD_ASSET_UPLOAD_SUBSCRIPTION, {
        variables: { uploadId },
        skip: !uploadId,

        onData: ({ data }) => {
            const fileData = data.data.adAssetUpload
            dispatch({ type: ACTIONS.UPDATE_FILE, payload: fileData })

            if (fileData.newId)
                dispatch({ type: ACTIONS.UPDATE_ID, payload: { oldId: fileData.fileId, newId: fileData.newId } })
        },
    })

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length === 0) return

        acceptedFiles.forEach((file) => {
            const payload = {
                id: ulid(),
                isNew: true,
                name: file.name,
                size: file.size,
                state: FILE_STATES.INITIAL,
                uploadedKB: 0,
                totalKB: (file.size / 1024).toFixed(2),
                type: file.type.startsWith('image') ? 'image' : 'video',
                file,
            }

            dispatch({ type: ACTIONS.ADD_FILE, payload })
        })
    }, [])

    const handleUpload = async () => {
        setUploadStarted(true)
        const uploadFilesData = files
            .filter((file) => file.isNew && file.state === FILE_STATES.INITIAL)
            .map((fileObj) => {
                dispatch({ type: ACTIONS.UPDATE_FILE, payload: { fileId: fileObj.id, state: FILE_STATES.UPLOADING } })

                return {
                    uuid: fileObj.id,
                    file: fileObj.file,
                }
            })

        uploadFiles({
            variables: {
                files: uploadFilesData,
                uploadId,
                campaignId,
                adId,
                replaceExisting: false,
                allowMultiple: true,
                formats: [],
                source: 'uploadForAi',
            },
        })
    }

    const removeFile = async (fileId) => {
        const file = files.find((f) => f.id === fileId)

        if (file.state === FILE_STATES.UPLOADED) deleteAdAsset({ variables: { id: fileId } })

        dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId } })
    }

    const handleCancelAll = async () => {
        const ids = files.filter((file) => file.state === FILE_STATES.UPLOADING).map((file) => file.id)
        await cancelUpload({ variables: { ids } })

        ids.forEach((id) => {
            dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId: id } })
        })
    }

    const handleCancel = async (fileId) => {
        await cancelUpload({ variables: { ids: [fileId] } })
        dispatch({ type: ACTIONS.REMOVE_FILE, payload: { fileId } })
    }

    if (adAssetsLoading)
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={true} />
            </div>
        )

    return (
        <Card className="border-0 d-flex flex-column gap-3 tw:px-6 tw:pt-6" style={{ backgroundColor: 'transparent' }}>
            <div className="upload-container" style={{ borderRadius: 2, backgroundColor: '#17273C' }}>
                <DropZone onDrop={onDrop} allowMultiple={true} campaignFormat={campaignFormat} />
            </div>
            <FilesList
                files={files}
                state={formState}
                removeFile={removeFile}
                handleCancel={handleCancel}
                handleUpload={handleUpload}
                handleCancelAll={handleCancelAll}
                footer={
                    <Button variant="secondary" className="mt-4" onClick={onCancel}>
                        Cancel
                    </Button>
                }
            />
        </Card>
    )
}

export default Upload
